import React, { useEffect, useState } from "react";
import { Loader2, ArrowLeft, Calendar, User, Clock, Share2 } from "lucide-react";

const CurrentAffairs = ({ wordpressUrl = "https://dimensionias.com/cms", categorySlug = "current-affairs" }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    const fetchCurrentAffairs = async () => {
      try {
        let apiUrl = `${wordpressUrl}/wp-json/wp/v2/posts?per_page=20&_embed=true`;
        
        // Find category ID by slug
        const categoryRes = await fetch(`${wordpressUrl}/wp-json/wp/v2/categories?slug=${categorySlug}`);
        if (categoryRes.ok) {
          const catData = await categoryRes.json();
          if (catData.length > 0) {
            apiUrl += `&categories=${catData[0].id}`;
          }
        }
        
        const response = await fetch(apiUrl);
        if (!response.ok) throw new Error("Failed to fetch Current Affairs posts");
        const data = await response.json();
        setPosts(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentAffairs();
  }, [wordpressUrl, categorySlug]);

  const openPost = (post) => {
    setSelectedPost(post);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closePost = () => {
    setSelectedPost(null);
  };

  const stripHtmlTags = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
  };

  const truncateText = (text, wordLimit = 35) => {
    const words = text.split(/\s+/);
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(' ') + '...';
  };

  if (loading) {
    return (
      <div className="pt-20 sm:pt-24 min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F0FDF7' }}>
        <div className="flex items-center space-x-2">
          <Loader2 className="animate-spin" style={{ color: '#2DD4BF' }} size={32} />
          <span className="text-lg font-medium" style={{ color: '#134E4A' }}>Loading Current Affairs...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-20 sm:pt-24 min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F0FDF7' }}>
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">Error: {error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-3 rounded-lg text-white font-semibold transition-colors shadow-md"
            style={{ backgroundColor: '#134E4A' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2DD4BF'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#134E4A'}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0FDF7' }}>
      {/* Full Page Article View */}
      {selectedPost ? (
        <div className="bg-white min-h-screen z-50 relative pb-24">
          {/* Sticky Navigation Bar */}
          <div className="bg-white border-b sticky top-0 z-40 shadow-sm">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
              <button
                onClick={closePost}
                className="flex items-center space-x-2 text-teal-900 font-semibold hover:text-teal-600 transition-colors"
              >
                <ArrowLeft size={20} />
                <span>Back to Current Affairs</span>
              </button>
              <div className="text-sm text-gray-500 hidden sm:block truncate max-w-xs" dangerouslySetInnerHTML={{ __html: selectedPost.title?.rendered }} />
            </div>
          </div>

          {/* Hero Banner Header */}
          <div className="relative w-full bg-teal-900 text-white py-16 md:py-20">
            {selectedPost._embedded?.["wp:featuredmedia"]?.[0]?.source_url && (
              <div className="absolute inset-0 overflow-hidden opacity-25">
                <img 
                  src={selectedPost._embedded["wp:featuredmedia"][0].source_url} 
                  alt={selectedPost.title?.rendered}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/images/blogs/default.jpg';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-teal-950 via-teal-900/80 to-teal-900/60" />
              </div>
            )}
            <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="mb-4">
                <span className="bg-teal-400 text-teal-950 px-4 py-1.5 rounded-full text-xs font-bold tracking-wide uppercase">
                  Daily Current Affairs
                </span>
              </div>
              <h1 
                className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white leading-tight mb-6"
                dangerouslySetInnerHTML={{ __html: selectedPost.title?.rendered }}
              />
              <div className="flex flex-wrap items-center gap-6 text-teal-100 text-sm md:text-base border-t border-teal-800/60 pt-6">
                <div className="flex items-center space-x-2">
                  <User size={18} className="text-teal-300" />
                  <span className="font-medium">{selectedPost._embedded?.author?.[0]?.name || "Dimension IAS Research Desk"}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar size={18} className="text-teal-300" />
                  <span>{new Date(selectedPost.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Full Article Content */}
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
            <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-10 md:p-12 border border-teal-50">
              {/* Rich Gutenberg HTML Content */}
              <div 
                dangerouslySetInnerHTML={{ __html: selectedPost.content?.rendered }}
                className="wordpress-content"
              />
              
              {/* Back Button & Actions */}
              <div className="mt-12 pt-8 border-t border-teal-100 flex flex-wrap items-center justify-between gap-4">
                <button 
                  onClick={closePost}
                  className="flex items-center space-x-2 bg-teal-800 hover:bg-teal-700 text-white px-6 py-2.5 rounded-full font-medium shadow-md transition-all"
                >
                  <ArrowLeft size={18} />
                  <span>Back to All Current Affairs</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Current Affairs Main List View */
        <div className="pt-20 sm:pt-24 pb-8 sm:pb-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="relative inline-block">
              <h2 
                className="text-4xl md:text-5xl font-bold mb-4 relative z-10"
                style={{ color: '#134E4A' }}
              >
                Current Affairs
                <div 
                  className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-20 h-1 rounded-full"
                  style={{ backgroundColor: '#2DD4BF' }}
                ></div>
              </h2>
            </div>
            <p 
              className="text-lg max-w-2xl mx-auto leading-relaxed mt-2"
              style={{ color: '#134E4A' }}
            >
              Stay updated with daily UPSC news analysis, editorial breakdowns, and key national/international updates.
            </p>
          </div>

          {/* No Posts Message */}
          {posts.length === 0 && (
            <div className="text-center py-12 bg-white rounded-3xl shadow-sm border border-teal-100 p-8">
              <p className="text-gray-500 text-lg">No Current Affairs posts found yet. Check back soon!</p>
            </div>
          )}

          {/* Posts List */}
          <div className="space-y-8">
            {posts.map((post) => {
              const plainExcerpt = stripHtmlTags(post.excerpt?.rendered || post.content?.rendered || '');
              const shortSummary = truncateText(plainExcerpt, 35);

              return (
                <div 
                  key={post.id} 
                  className="bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 overflow-hidden border-2 group cursor-pointer"
                  style={{ borderColor: '#E0F2F1' }}
                  onClick={() => openPost(post)}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = '#2DD4BF';
                    e.currentTarget.style.backgroundColor = '#FEFFFE';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = '#E0F2F1';
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  {/* Featured Image */}
                  {post._embedded?.["wp:featuredmedia"]?.[0]?.source_url && (
                    <div className="relative overflow-hidden">
                      <img 
                        src={post._embedded["wp:featuredmedia"][0].source_url} 
                        alt={post._embedded["wp:featuredmedia"][0].alt_text || post.title?.rendered}
                        className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/images/blogs/default.jpg';
                        }}
                      />
                      <div 
                        className="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity duration-500"
                        style={{ 
                          background: 'linear-gradient(to top, rgba(45, 212, 191, 0.6), rgba(19, 78, 74, 0.2))' 
                        }}
                      />
                    </div>
                  )}

                  {/* Content Preview */}
                  <div className="p-8">
                    <h3 
                      className="text-2xl font-bold mb-4 leading-tight transition-colors group-hover:text-teal-600"
                      style={{ color: '#134E4A' }}
                      dangerouslySetInnerHTML={{ __html: post.title?.rendered }}
                    />
                    
                    {/* Post Meta */}
                    <div className="flex items-center space-x-4 mb-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1.5">
                        <Calendar size={16} className="text-teal-600" />
                        <span>
                          {new Date(post.date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                      {post._embedded?.author?.[0]?.name && (
                        <div className="flex items-center space-x-1.5">
                          <User size={16} className="text-teal-600" />
                          <span>By {post._embedded.author[0].name}</span>
                        </div>
                      )}
                    </div>

                    <p className="text-gray-600 mb-6 leading-relaxed text-base sm:text-lg">
                      {shortSummary}
                    </p>

                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        openPost(post);
                      }}
                      className="inline-flex items-center px-6 py-3 rounded-full font-semibold text-white transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
                      style={{ backgroundColor: '#134E4A' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2DD4BF'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = '#134E4A'}
                    >
                      <span>Read Full Update</span>
                      <ArrowLeft className="ml-2 w-5 h-5 transform rotate-180" />
                    </button>
                  </div>

                  {/* Bottom accent line */}
                  <div 
                    className="h-1 w-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ 
                      background: 'linear-gradient(to right, #134E4A, #2DD4BF, #134E4A)' 
                    }}
                  />
                </div>
              );
            })}
          </div>

          <div className="py-12"></div>
        </div>
      )}
    </div>
  );
};

export default CurrentAffairs;